import { useQuery } from '@tanstack/react-query';
import { ApiError } from 'src/error/ApiError';

// An overview is a briefing about a single subject — a family or a member.
// It is deliberately not tied to any one screen (e.g. a meeting), so the same
// endpoint and hook can back a family view, a member view, or a meeting.
export type OverviewSubject =
  | { kind: 'family'; familyId: string }
  | { kind: 'client'; clientId: string };

type Overview = {
  summary: string;
  lastMeetingAt: string | null;
  generatedAt: string | null;
};

function overviewQueryKey(subject: OverviewSubject): readonly unknown[] {
  return subject.kind === 'family'
    ? ['families', subject.familyId, 'overview']
    : ['clients', subject.clientId, 'overview'];
}

export const useOverview = (subject: OverviewSubject | null) => {
  const { data, isLoading, error } = useQuery<Overview>({
    queryKey: subject ? overviewQueryKey(subject) : ['overview', 'disabled'],
    enabled: !!subject,
    retry: false,
    // An overview is an expensive per-request build (a full snapshot plus an
    // LLM completion, uncached server-side). Keep it stable for the life of the
    // screen so re-mounting or alt-tabbing back mid-preparation doesn't re-run
    // generation and change the summary under the advisor.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    // The overview is generated best-effort; a missing one is a normal empty
    // state rather than an error worth reporting.
    meta: { expectedStatuses: [404] },
  });

  // A 404 is the normal "no overview yet" path — fetchWrapper still throws it,
  // so strip it here rather than surfacing it as a failure to callers.
  const isMissing = error instanceof ApiError && error.status === 404;

  return {
    overview: data,
    isLoading,
    error: isMissing ? undefined : error,
  };
};
